<template>
  <div class="price-alerts-ui-container">
    <div class="price-alerts-ui-header">
      <span class="price-alerts-ui-header-title">
        <i class="fas fa-fw fa-bells"></i>
        Price Alerts
      </span>
      <a
        class="sc3-button sc3-button-grey sc3-button-small price-alerts-ui-header-workbench-link"
        href="/h-mem/price-alert-workbench.html"
        target="_blank"
      >
        Go to Price Alert Workbench&nbsp;
        <i class="fas fa-fw fa-arrow-circle-right"></i>
      </a>
    </div>
    <div class="create-alert-link-container" v-if="userHasZeroAlerts">
      <a href="/h-mem/price-alert-workbench.html" class="create-alert-link" target="_blank">Create Your First Price Alert!</a>
    </div>
    <div class="data-table-container" v-else-if="priceAlerts">
      <DataTable
        :rows="priceAlerts"
        :columns="DATA_TABLE_COLUMNS"
      />
    </div>
    <div class="loading-indicator" v-else-if="advancedAlertsAndPriceAlertsLoadingState === LoadingState.LOADING">
      <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
    </div>
    <div v-else-if="advancedAlertsAndPriceAlertsLoadingState === LoadingState.ERROR">
      Sorry, we failed to load your price alerts. We will try again in a few seconds.
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { PriceAlert, useAlerts } from '@/composables/useAlerts';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useUniqueIds } from '@/composables/useUniqueIds';
  import DataTable from '@/components/custom-widgets/DataTable.vue';
  import { DataTableColumn } from '@/interfaces/DataTable';
  import { LoadingState } from '@/enums/loading-state';

  const {
    deletePriceAlert,
    loadAdvancedAlertsAndPriceAlerts,
    priceAlerts,
    advancedAlertsAndPriceAlertsLoadingState,
    pausePriceAlert,
    startPriceAlert
  } = useAlerts();
  const { addRedActionResultMessage } = useActionResultMessages();
  const { getPositiveUniqueId } = useUniqueIds();

  const userHasZeroAlerts = computed(() => {
    return priceAlerts.value?.length === 0;
  });

  const DATA_TABLE_COLUMNS: DataTableColumn<PriceAlert>[] = [
    {
      id: getPositiveUniqueId(),
      title: 'Alert Name',
      keyForRendering: 'name',
      keyForSorting: 'name',
      onClick: handleAlertNameClick,
      initialSortDirection: 'ascending'
    },
    {
      id: getPositiveUniqueId(),
      title: 'Last Triggered',
      keyForRendering: 'lastFiredDateString',
      keyForSorting: 'lastFiredDateObject',
      sortOnInit: 'descending'
    },
    {
      id: getPositiveUniqueId(),
      title: 'Last Price',
      keyForRendering: 'price',
      keyForSorting: 'price',
      width: 100,
      isNumeric: true
    },
    {
      id: getPositiveUniqueId(),
      title: 'Delta',
      keyForRendering: 'delta',
      keyForSorting: 'delta',
      width: 100,
      isNumeric: true
    },
    {
      id: getPositiveUniqueId(),
      title: 'Message',
      keyForRendering: 'notes',
      getTdClass: (row: PriceAlert) => 'message'
    },
    {
      id: getPositiveUniqueId(),
      title: 'Status',
      keyForRendering: 'status',
      keyForSorting: 'status',
      getTdClass: (row: PriceAlert) => row.isPaused ? 'is-paused' : 'is-running',
      width: 100
    },
    {
      id: getPositiveUniqueId(),
      onClick: handlePlayPauseClick,
      getIconClass: (row: PriceAlert) => row.isPaused ? 'fas fa-fw fa-play' : 'fas fa-fw fa-pause',
      getTdClass: (row: PriceAlert) => 'action',
      width: 45
    },
    {
      id: getPositiveUniqueId(),
      onClick: handleDeleteClick,
      getIconClass: (row: PriceAlert) => 'fas fa-fw fa-trash-alt',
      getTdClass: (row: PriceAlert) => 'action',
      width: 45
    }
  ];

  function handleAlertNameClick(row: PriceAlert) {
    window.open(`/h-mem/price-alert-workbench.html#id=${row.recid}`, '_blank');
  }

  async function handleDeleteClick(row: PriceAlert) {
    if (!confirm('Are you sure you wish to delete this alert?')) {
      return;
    }

    try {
      await deletePriceAlert(row.recid);
    } catch (error) {
      addRedActionResultMessage('Sorry, there was a problem deleting your alert, please try again.');
    }

    await loadAdvancedAlertsAndPriceAlerts();

    if (advancedAlertsAndPriceAlertsLoadingState.value === LoadingState.ERROR) {
      addRedActionResultMessage('Sorry, there was a problem refreshing your alerts. We will try again in a few seconds.');
    }
  }

  async function handlePlayPauseClick(row: PriceAlert) {
    try {
      if (row.isPaused) {
        await startPriceAlert(row.recid);
      } else {
        await pausePriceAlert(row.recid);
      }
    } catch (error) {
      addRedActionResultMessage(`Sorry, there was a problem ${row.isPaused ? 'starting' : 'pausing'} your alert, please try again.`);
    }

    await loadAdvancedAlertsAndPriceAlerts();

    if (advancedAlertsAndPriceAlertsLoadingState.value === LoadingState.ERROR) {
      addRedActionResultMessage('Sorry, there was a problem refreshing your alerts. We will try again in a few seconds.');
    }
  }
</script>

<style scoped>
  .price-alerts-ui-container {
    margin: 0px;
    padding: 6px;
  }

  .price-alerts-ui-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 0px 6px 0px;
    margin: 0px 0px 6px 0px;
    border-bottom: 2px solid #c0ced8;
  }

  .price-alerts-ui-header-title {
    font-size: 16px;
    line-height: 1.5;
    font-weight: 700;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .price-alerts-ui-header-workbench-link {
    margin: 0px;
  }

  .loading-indicator {
    text-align: center;
  }

  .create-alert-link-container {
    padding: 10px;
    text-align: center;
  }

  .create-alert-link {
    text-decoration: none;
    font-size: 16px;
    font-weight: bold;
    color: #0f4d8a;
  }
</style>

<style>
  .price-alerts-ui-container table.sc3-table tbody tr td {
    font-weight: 400 !important;
  }

  .price-alerts-ui-container table.sc3-table tbody tr td.is-paused {
    text-align: center;
    color: #cc000e;
  }

  .price-alerts-ui-container table.sc3-table tbody tr td.is-running {
    text-align: center;
    color: #219155;
  }

  .price-alerts-ui-container table.sc3-table tbody tr td.action {
    text-align: center;
  }

  .price-alerts-ui-container table.sc3-table tbody tr td.action button {
    color: #2d3339;
  }

  .price-alerts-ui-container table.sc3-table tbody tr td.message {
    color: #707f8f;
  }

  .price-alerts-ui-container button, a {
    text-align: left;
  }
</style>
